import * as React from 'react'
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Grid,
    MenuItem,
    Stack,
    TextField,
    Typography
} from '@mui/material'
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded'
import EmojiEventsRoundedIcon from '@mui/icons-material/EmojiEventsRounded'
import TrendingUpRoundedIcon from '@mui/icons-material/TrendingUpRounded'
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded'
import BoltRoundedIcon from '@mui/icons-material/BoltRounded'
import ReceiptLongRoundedIcon from '@mui/icons-material/ReceiptLongRounded'

const cardSx = {
    borderRadius: '16px',
    border: '1px solid rgba(129, 146, 178, 0.18)',
    background: 'linear-gradient(180deg, rgba(21, 33, 55, 0.62) 0%, rgba(18, 29, 48, 0.5) 100%)',
    boxShadow: '0 8px 24px rgba(7, 13, 26, 0.18)',
    p: 2.5
}

const statCardSx = (color = '#2f7cff') => ({
    ...cardSx,
    borderColor: `${color}22`
})

function StatCard({ icon, label, value, color = '#2f7cff', sub }) {
    return (
        <Box sx={statCardSx(color)}>
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 0.8 }}>
                <Box sx={{ color, fontSize: 28 }}>{icon}</Box>
                <Typography sx={{ color: '#9fb0cf', fontSize: '0.92rem', fontWeight: 600 }}>{label}</Typography>
            </Stack>
            <Typography sx={{ color: '#f5f7ff', fontSize: '2rem', fontWeight: 800, lineHeight: 1 }}>
                {value}
            </Typography>
            {sub && (
                <Typography sx={{ color: '#7f8fae', fontSize: '0.88rem', mt: 0.5 }}>
                    {sub}
                </Typography>
            )}
        </Box>
    )
}

function DayRow({ day }) {
    const winRate = day.winRate ?? (day.attacks > 0 ? Math.round((day.wins / day.attacks) * 100) : 0)
    const winColor = winRate >= 70 ? '#4caf50' : winRate >= 40 ? '#ff9800' : '#f44336'

    return (
        <Box sx={{
            px: 2.2,
            py: 1.6,
            borderRadius: '12px',
            border: '1px solid rgba(255,255,255,0.06)',
            backgroundColor: 'rgba(255,255,255,0.025)',
            mb: 1
        }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ sm: 'center' }} justifyContent="space-between" spacing={1}>
                <Typography sx={{ color: '#a8b8d8', fontWeight: 700, fontSize: '0.95rem' }}>
                    {day.date}
                </Typography>
                <Stack direction="row" spacing={1.2} alignItems="center" flexWrap="wrap">
                    <Chip
                        size="small"
                        label={`${day.attacks} atacuri`}
                        sx={{ backgroundColor: 'rgba(47, 124, 255, 0.18)', color: '#7eb3ff', fontWeight: 700 }}
                    />
                    <Chip
                        size="small"
                        label={`${day.wins} castigate`}
                        sx={{ backgroundColor: 'rgba(76, 175, 80, 0.18)', color: '#81c784', fontWeight: 700 }}
                    />
                    {day.losses > 0 && (
                        <Chip
                            size="small"
                            label={`${day.losses} pierdute`}
                            sx={{ backgroundColor: 'rgba(244, 67, 54, 0.18)', color: '#ef9a9a', fontWeight: 700 }}
                        />
                    )}
                    <Box sx={{
                        px: 1.2,
                        py: 0.3,
                        borderRadius: '8px',
                        backgroundColor: `${winColor}22`,
                        border: `1px solid ${winColor}44`
                    }}>
                        <Typography sx={{ color: winColor, fontWeight: 800, fontSize: '0.9rem' }}>
                            {winRate}% victorie
                        </Typography>
                    </Box>
                </Stack>
            </Stack>
        </Box>
    )
}

const normalizeSelectableUsers = value =>
    (Array.isArray(value) ? value : [])
        .map(user => ({
            id: Number(user?.id ?? NaN),
            name: String(user?.name ?? '')
        }))
        .filter(user => Number.isFinite(user.id) && user.id > 0)

export const mergeBattleReportAccountOptions = (responseUsers, liveUsers) => {
    const optionsById = new Map()
    normalizeSelectableUsers(responseUsers).forEach(user => optionsById.set(user.id, user))
    normalizeSelectableUsers(liveUsers).forEach(user => optionsById.set(user.id, user))
    return Array.from(optionsById.values())
        .sort((left, right) => left.name.localeCompare(right.name) || left.id - right.id)
}

export default function BattleReportsPanel({ users = [] }) {
    const [reports, setReports] = React.useState(null)
    const [loading, setLoading] = React.useState(true)
    const [error, setError] = React.useState(false)
    const [selectedUserId, setSelectedUserId] = React.useState('')

    const selectableUsers = React.useMemo(
        () => normalizeSelectableUsers(users),
        [users]
    )

    const responseUsers = React.useMemo(
        () => normalizeSelectableUsers(reports?.users),
        [reports?.users]
    )

    const accountOptions = React.useMemo(
        () => mergeBattleReportAccountOptions(responseUsers, selectableUsers),
        [responseUsers, selectableUsers]
    )

    React.useEffect(() => {
        const serverSelectedUserId = reports?.selectedUserId
        if (
            selectedUserId === '' &&
            serverSelectedUserId !== undefined &&
            serverSelectedUserId !== null
        ) {
            setSelectedUserId(String(serverSelectedUserId))
        }
    }, [reports?.selectedUserId, selectedUserId])

    React.useEffect(() => {
        if (accountOptions.length === 0) {
            if (selectedUserId !== '')
                setSelectedUserId('')
            return
        }

        const hasSelectedUser = accountOptions.some(user => String(user.id) === String(selectedUserId))
        if (!hasSelectedUser)
            setSelectedUserId(String(accountOptions[0].id))
    }, [accountOptions, selectedUserId])

    const loadReports = React.useCallback(async () => {
        try {
            setLoading(true)
            setError(false)
            const query = selectedUserId ? `?userId=${encodeURIComponent(String(selectedUserId))}` : ''
            const response = await fetch(`/battle-reports${query}`, { credentials: 'same-origin' })
            if (!response.ok)
                throw new Error(`HTTP ${response.status}`)
            const data = await response.json()
            setReports(data)
        } catch (e) {
            console.warn('Failed to load battle reports', e)
            setError(true)
        } finally {
            setLoading(false)
        }
    }, [selectedUserId])

    React.useEffect(() => {
        if (accountOptions.length > 0 && !selectedUserId)
            return

        loadReports()
    }, [loadReports, accountOptions.length, selectedUserId])

    const summary = reports?.summary
    const days = reports?.days ?? []
    const activeAttackModules = Array.isArray(reports?.activeAttackModules)
        ? reports.activeAttackModules
        : []
    const resolvedSelectedUserId = String(selectedUserId || reports?.selectedUserId || '')
    const selectedUserName =
        accountOptions.find(user => String(user.id) === resolvedSelectedUserId)?.name ||
        String(reports?.selectedUserName ?? '') ||
        ''

    return (
        <Box>
            <Stack
                direction={{ xs: 'column', md: 'row' }}
                alignItems={{ md: 'center' }}
                justifyContent="space-between"
                spacing={1.5}
                sx={{ mb: 3 }}
            >
                <Box>
                    <Typography sx={{ color: '#f5f7ff', fontSize: '1.35rem', fontWeight: 800 }}>
                        Rapoarte Batalii
                    </Typography>
                    <Typography sx={{ color: '#7f8fae', fontSize: '0.95rem', mt: 0.4 }}>
                        Statistici zilnice batalii - urmarite automat de bot
                    </Typography>
                    {selectedUserName && (
                        <Typography sx={{ color: '#9fb0cf', fontSize: '0.92rem', mt: 0.6 }}>
                            Cont: <strong>{selectedUserName}</strong>
                        </Typography>
                    )}
                </Box>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.2} sx={{ width: { xs: '100%', md: 'auto' } }}>
                    {accountOptions.length > 0 && (
                        <TextField
                            select
                            size="small"
                            value={resolvedSelectedUserId}
                            onChange={event => setSelectedUserId(String(event.target.value))}
                            sx={{
                                minWidth: { xs: '100%', sm: 260 },
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: '10px',
                                    color: '#f5f7ff'
                                },
                                '& .MuiOutlinedInput-notchedOutline': {
                                    borderColor: 'rgba(112, 136, 174, 0.4)'
                                }
                            }}
                        >
                            {accountOptions.map(user => (
                                <MenuItem key={user.id} value={String(user.id)}>
                                    {user.name}
                                </MenuItem>
                            ))}
                        </TextField>
                    )}
                    <Button
                        variant="outlined"
                        startIcon={<RefreshRoundedIcon />}
                        onClick={loadReports}
                        disabled={loading}
                        sx={{
                            borderRadius: '10px',
                            borderColor: 'rgba(112, 136, 174, 0.4)',
                            color: '#c8d6f0',
                            textTransform: 'none',
                            fontWeight: 700
                        }}
                    >
                        Refresh
                    </Button>
                </Stack>
            </Stack>

            {loading ? (
                <Stack alignItems="center" justifyContent="center" sx={{ py: 8 }}>
                    <CircularProgress />
                </Stack>
            ) : error ? (
                <Box sx={{ ...cardSx, borderColor: 'rgba(244,67,54,0.3)' }}>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                        <WarningAmberRoundedIcon sx={{ color: '#f44336', fontSize: 28 }} />
                        <Box>
                            <Typography sx={{ color: '#f5f7ff', fontWeight: 700 }}>Nu s-au putut incarca rapoartele</Typography>
                            <Typography sx={{ color: '#9fb0cf', fontSize: '0.9rem', mt: 0.3 }}>
                                Verifica conexiunea si incearca din nou.
                            </Typography>
                        </Box>
                    </Stack>
                </Box>
            ) : days.length === 0 ? (
                <Box sx={cardSx}>
                    <Stack spacing={1.5} alignItems="center" sx={{ py: 4 }}>
                        <ReceiptLongRoundedIcon sx={{ fontSize: 48, color: '#4a5878' }} />
                        <Typography sx={{ color: '#f5f7ff', fontWeight: 700, fontSize: '1.05rem' }}>
                            Nicio batalie inregistrata inca
                        </Typography>
                        <Typography sx={{ color: '#7f8fae', fontSize: '0.92rem', textAlign: 'center', maxWidth: 440 }}>
                            Rapoartele pentru contul selectat vor aparea automat dupa ce bot-ul trimite atacuri si primeste
                            rezultatele bataliilor. Asigura-te ca pluginul <strong>BattleLogger</strong> este activ.
                        </Typography>
                    </Stack>
                </Box>
            ) : (
                <Stack spacing={3}>
                    {summary && (
                        <Grid container spacing={2}>
                            <Grid item xs={6} md={3}>
                                <StatCard
                                    icon={<BoltRoundedIcon />}
                                    label="Total Atacuri"
                                    value={Number(summary.totalAttacks ?? 0).toLocaleString()}
                                    color="#2f7cff"
                                    sub={`pe ${Number(summary.daysTracked ?? 0)} zile`}
                                />
                            </Grid>
                            <Grid item xs={6} md={3}>
                                <StatCard
                                    icon={<EmojiEventsRoundedIcon />}
                                    label="Batalii Castigate"
                                    value={Number(summary.totalWins ?? 0).toLocaleString()}
                                    color="#4caf50"
                                    sub={`${Number(summary.avgWinRate ?? 0)}% rata victorie`}
                                />
                            </Grid>
                            <Grid item xs={6} md={3}>
                                <StatCard
                                    icon={<WarningAmberRoundedIcon />}
                                    label="Batalii Pierdute"
                                    value={Number(summary.totalLosses ?? 0).toLocaleString()}
                                    color="#f44336"
                                />
                            </Grid>
                            <Grid item xs={6} md={3}>
                                <StatCard
                                    icon={<TrendingUpRoundedIcon />}
                                    label="Rata Victorie"
                                    value={`${Number(summary.avgWinRate ?? 0)}%`}
                                    color={summary.avgWinRate >= 70 ? '#4caf50' : summary.avgWinRate >= 40 ? '#ff9800' : '#f44336'}
                                    sub="medie totala"
                                />
                            </Grid>
                        </Grid>
                    )}

                    <Box sx={cardSx}>
                        <Stack
                            direction={{ xs: 'column', sm: 'row' }}
                            alignItems={{ sm: 'center' }}
                            justifyContent="space-between"
                            spacing={1}
                            sx={{ mb: activeAttackModules.length > 0 ? 1.5 : 0 }}
                        >
                            <Stack direction="row" spacing={1} alignItems="center">
                                <BoltRoundedIcon sx={{ color: '#f5c451', fontSize: 22 }} />
                                <Typography sx={{ color: '#f5f7ff', fontSize: '1rem', fontWeight: 800 }}>
                                    Module de atac active
                                </Typography>
                            </Stack>
                            <Typography sx={{ color: '#7f8fae', fontSize: '0.88rem' }}>
                                {activeAttackModules.length} active pentru contul selectat
                            </Typography>
                        </Stack>
                        {activeAttackModules.length > 0 ? (
                            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                                {activeAttackModules.map(module => (
                                    <Chip
                                        key={module.key || module.name}
                                        size="small"
                                        label={module.name}
                                        sx={{
                                            backgroundColor: 'rgba(245, 196, 81, 0.12)',
                                            border: '1px solid rgba(245, 196, 81, 0.3)',
                                            color: '#f4d27d',
                                            fontWeight: 700
                                        }}
                                    />
                                ))}
                            </Stack>
                        ) : (
                            <Typography sx={{ color: '#7f8fae', fontSize: '0.92rem' }}>
                                Niciun modul de atac nu este activ pentru acest bot.
                            </Typography>
                        )}
                    </Box>

                    <Box>
                        <Typography sx={{ color: '#9fb0cf', fontSize: '0.95rem', fontWeight: 700, mb: 1.5 }}>
                            Istoricul Zilnic
                        </Typography>
                        {days.map(day => (
                            <DayRow key={day.date} day={day} />
                        ))}
                    </Box>
                </Stack>
            )}
        </Box>
    )
}
